import React, {
  ForwardRefRenderFunction, PropsWithChildren, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState
} from 'react'
import { FileError, FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box, BoxProps, Button, CircularProgress, Fade, IconButton, Snackbar, Tooltip, Typography
} from '@mui/material'
import CopyrightIcon from '@mui/icons-material/Copyright'
import CropIcon from '@mui/icons-material/Crop'
import { FileCopyright } from '@typings/graphql'
// import DeleteIcon from '@mui/icons-material/Delete'

import { FilePreview } from './FilePreview'
import { ContentFile } from './content-type-editors/mutation-helper/file'
import { CopyrightEditDialog } from './dialogs/CopyrightEditDialog'
import { ImageCropDialog } from './dialogs/ImageCropDialog'

export type FileFormats = {
  'image/*'?: string[],
  'video/*'?: string[],
  'audio/*'?: string[],
  'text/html'?: string[],
  'text/plain'?: string[],
  'text/csv'?: string[],
  'text/vtt'?: string[],
  'application/json'?: string[],
  'application/zip'?: string[]
  'application/object'?: string[],
  'application/*'?: string[],
}

export type FileDragDropProps = PropsWithChildren & BoxProps & {
  preview?: boolean
  accept: FileFormats
  copyright?: boolean
  onFileChanged: (file: FileWithCopyright | null) => void
  dragPlaceholder?: string
  dropPlaceholder?: string
  initialFile?: Partial<ContentFile> | null
  loading?: boolean
  maxFileSize?: number
  // Local fallback image shown when no real file exists yet (e.g. dummy placeholder)
  placeholderSrc?: string
};

export type FileDragDropHandler = {
  open: () => void,
  reset: () => void
}

type FileDragDropRenderFn = ForwardRefRenderFunction<FileDragDropHandler, FileDragDropProps>

type FileWithCopyright = File & {
  copyright?: FileCopyright | null
}

const formatBytes = (bytes: number): string => {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(0)} GB`
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(0)} MB`
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} KB`
  return `${bytes} B`
}

const FileDragDrop: FileDragDropRenderFn = (props, ref) => {
  const {
    onFileChanged,
    accept,
    copyright,
    dragPlaceholder,
    dropPlaceholder,
    loading,
    initialFile,
    preview,
    maxFileSize = 20000000,
    placeholderSrc,
    children,
    ...boxProps
  } = props
  const { t } = useTranslation()

  const initialRender = useRef(true)

  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)
  const [errorMessages, setErrorMessages] = useState<FileError[]>()
  const [showCopyrightDialog, setShowCopyrightDialog] = useState(false)
  const [showCropDialog, setShowCropDialog] = useState(false)
  const [file, setFile] = useState<FileWithCopyright | undefined>(initialFile as any)

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles?: FileRejection[]) => {
    if (rejectedFiles) {
      rejectedFiles.forEach(({ errors }) => {
        console.log(errors)
        setErrorMessages(errors)
        setSnackbarOpen(true)
      })
    }

    setFile(acceptedFiles[0])
  }, [])

  const openCopyrightDialog = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowCopyrightDialog(true)
  }

  const openCropDialog = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowCropDialog(true)
  }

  const onCropped = (croppedFile: File) => {
    setFile(croppedFile as FileWithCopyright)
  }

  /** URL used as input for the crop dialog — prefer a blob ObjectURL for newly uploaded files. */
  const cropImageSrc = useMemo(() => {
    if (file instanceof File) return URL.createObjectURL(file)
    if (initialFile?.url) return initialFile.url
    return null
  }, [file, initialFile])

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false

      return
    }

    onFileChanged(file ?? null)
  }, [file])

  const updateCopyright = (newCopyright: FileCopyright | null) => {
    setFile((prev) => {
      if (!prev) {
        return prev
      }

      if (newCopyright) {
        delete newCopyright.__typename
      }

      return {
        ...prev,
        copyright: newCopyright
      }
    })

    setShowCopyrightDialog(false)
  }

  const previewFile = useMemo(() => {
    if (!preview || (!file && !initialFile)) {
      return null
    }

    if (file instanceof File) {
      return {
        url: URL.createObjectURL(file),
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size
      }
    }

    if (!initialFile) {
      return null
    }

    return {
      url: initialFile.url,
      fileName: initialFile.fileName,
      mimeType: initialFile.mimeType,
      fileSize: initialFile.fileSize
    }
  }, [file, initialFile])

  const {
    getRootProps, getInputProps, isDragActive, open
  } = useDropzone({
    accept,
    maxSize: maxFileSize,
    multiple: false,
    onDrop
  })

  useImperativeHandle(ref, () => ({
    open,
    reset: () => onDrop([])
  }))

  return (
    <>
      <Box display="flex" justifyContent="center" {...getRootProps()} {...boxProps} position="relative" overflow="hidden">
        <Button
          variant="text"
          sx={{
            border: 'dashed 2px',
            borderColor: isDragActive ? 'primary.main' : 'grey.500',
            bgcolor: isDragActive ? 'action.hover' : 'transparent',
            transition: 'border-color 120ms, background-color 120ms',
            height: 'auto',
            maxHeight: '60vh',
            width: '100%',
            '&:hover': {
              borderColor: isDragActive ? 'primary.main' : 'grey.700'
            },
            display: 'flex',
            alignItem: 'center',
            justifyContent: 'center'
          }}
        >
          { previewFile
            ? <Box width="100%" height="100%" p={1}><FilePreview preview={previewFile} /></Box>
            : placeholderSrc
              ? <Box width="100%" height="100%" p={1} display="flex" justifyContent="center">
                <img
                  src={placeholderSrc}
                  alt="placeholder"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Box>
              : children }
        </Button>

        <Box position="relative">
          <input {...getInputProps()} />
          {
        isDragActive
          ? dropPlaceholder && <Typography>{dropPlaceholder}</Typography>
          : dragPlaceholder && <Typography>{dragPlaceholder}</Typography>
      }
        </Box>

        {copyright && <Box position="absolute" top={4} right={4}>
          <IconButton onClick={openCopyrightDialog}
            size='small'
          >
            <CopyrightIcon />
          </IconButton>
        </Box>}

        {cropImageSrc && 'image/*' in accept && (
          <Box position="absolute" top={4} left={4}>
            <Tooltip title="Bild zuschneiden">
              <IconButton
                size="small"
                onClick={openCropDialog}
                sx={{ backgroundColor: 'rgba(0,0,0,0.4)', color: '#fff', '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' } }}
              >
                <CropIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* {previewFile && <Box position="absolute" bottom={4} left={4}>
          <IconButton
            size='small'
            onClick={(e) => {
              setFile(undefined)
              e.stopPropagation()
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>} */}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={() => {
            setSnackbarOpen(false)
          }}
        >
          <Alert severity="error">
            {errorMessages?.some((e) => e.code === 'file-too-large')
              ? <Typography>{t('errors.fileTooLarge', { size: formatBytes(maxFileSize) })}</Typography>
              : <Typography>{t('errors.invalidFileType')}</Typography>}
          </Alert>
        </Snackbar>

        <Fade in={loading}>
          <Box
            position="absolute"
            left={-10}
            top={-10}
            right={-10}
            bottom={-10}
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ backdropFilter: 'blur(5px)' }}
          >
            <CircularProgress />
          </Box>
        </Fade>
      </Box>

      <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
        {t('common.maxFileSize', { size: formatBytes(maxFileSize) })}
      </Typography>

      {copyright && <CopyrightEditDialog
        copyright={file?.copyright}
        open={showCopyrightDialog}
        onClose={() => setShowCopyrightDialog(false)}
        onSubmit={updateCopyright}
      />}

      {cropImageSrc && (
        <ImageCropDialog
          open={showCropDialog}
          imageSrc={cropImageSrc}
          onClose={() => setShowCropDialog(false)}
          onCropped={onCropped}
        />
      )}
    </>
  )
}

export default React.forwardRef(FileDragDrop)
